import React, { useEffect, useRef, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { AnimatePresence, motion, useIsPresent } from "framer-motion";
import { User, createNewUser } from "../types/user";
import { useRosterEditor } from "../hooks/useRosterEditor";
import RecentMates, { useKnownPeople } from "./RecentMates";
import "./PillButton.scss";
import "./MatesTable.scss";

// Same feel as the item rows: added rows grow in, deleted rows slide left and
// fade while collapsing. A <tr> can't animate its own height, so each cell's
// content sits in a .cell div that collapses; the row fades and slides.
const rowVariants = {
  hidden: { opacity: 0, x: 0 },
  visible: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -24 },
};
const cellVariants = {
  hidden: { height: 0 },
  visible: { height: "auto" },
  exit: { height: 0 },
};
const rowTransition = { duration: 0.2, ease: "easeOut" } as const;

// A row that ignores taps while it animates out — its handlers still hold its
// old index, so a second tap on its trash would delete whoever moved up.
const AnimatedRow: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isPresent = useIsPresent();
  return (
    <motion.tr
      variants={rowVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      transition={rowTransition}
      style={{ pointerEvents: isPresent ? undefined : "none" }}
    >
      {children}
    </motion.tr>
  );
};

// a cell's content, collapsing with its row
const Cell: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <motion.div
    className="cell"
    variants={cellVariants}
    transition={rowTransition}
  >
    {children}
  </motion.div>
);

interface MatesTableProps {
  users: User[];
  setUsers: React.Dispatch<React.SetStateAction<User[]>>;
  whichUserPaidIndex: number;
  setWhichUserPaidIndex: React.Dispatch<React.SetStateAction<number>>;
}

// Pre-share roster editor on the page (above "Upload the Check"): one editable row per mate, with
// "Me" pinned as row 1. Edits land on the roster as you type and are
// trimmed/deduped/remembered on blur. Rows left without a name are dropped when
// the link is created.
const MatesTable: React.FC<MatesTableProps> = ({
  users,
  setUsers,
  whichUserPaidIndex,
  setWhichUserPaidIndex,
}) => {
  const { patchUser, commitUser, removeUser } = useRosterEditor({
    users,
    setUsers,
    whichUserPaidIndex,
    setWhichUserPaidIndex,
  });
  const { knownPeople, suggestions } = useKnownPeople(users);

  // inputs by `${userId}:name|venmo`, for Enter-to-advance and focusing new rows
  const inputs = useRef(new Map<string, HTMLInputElement>());
  const bindInput = (key: string) => (el: HTMLInputElement | null) => {
    if (el) inputs.current.set(key, el);
    else inputs.current.delete(key);
  };

  // a just-added row's name input, focused once it has rendered
  const [focusUserId, setFocusUserId] = useState<string | null>(null);
  useEffect(() => {
    if (!focusUserId) return;
    const el = inputs.current.get(`${focusUserId}:name`);
    if (!el) return;
    el.focus();
    el.scrollIntoView({ block: "nearest" });
    setFocusUserId(null);
  }, [focusUserId, users]);

  const addRow = () => {
    const newUser = createNewUser("");
    setUsers([...users, newUser]);
    setFocusUserId(newUser.id);
  };

  // a tapped "Recent" chip fills a trailing blank row if there is one
  const handlePick = (person: User) => {
    const lastIndex = users.length - 1;
    const last = users[lastIndex];
    const lastIsBlank =
      lastIndex > 0 && !last.name.trim() && !last.venmo?.trim();
    setUsers(
      lastIsBlank
        ? users.map((u, i) => (i === lastIndex ? person : u))
        : [...users, person],
    );
  };

  // Enter: name -> same row's venmo -> next row's name (adding one at the end)
  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number,
    field: "name" | "venmo",
  ) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    if (field === "name") {
      inputs.current.get(`${users[index].id}:venmo`)?.focus();
    } else if (index === users.length - 1) {
      addRow();
    } else {
      inputs.current.get(`${users[index + 1].id}:name`)?.focus();
    }
  };

  return (
    <div className="mates-table">
      <button
        type="button"
        className="pill-button add-mate-button"
        onClick={addRow}
      >
        <Plus size={16} />
        Add your Mate
      </button>

      <div className="mates-table-scroll">
        <table>
          <thead>
            <tr>
              <th className="col-num">#</th>
              <th className="col-name">Name</th>
              <th>Venmo (optional)</th>
              <th className="col-action" aria-label="Remove" />
            </tr>
          </thead>
          <tbody>
            {/* initial={false}: existing rows don't grow in on page load */}
            <AnimatePresence initial={false}>
              {users.map((user, index) => (
                <AnimatedRow key={user.id}>
                  <td className="col-num">
                    <Cell>{index + 1}</Cell>
                  </td>
                  <td>
                    <Cell>
                      <input
                        ref={bindInput(`${user.id}:name`)}
                        placeholder={index === 0 ? "Your name" : "Name"}
                        value={user.name}
                        onChange={(e) =>
                          patchUser(index, { name: e.target.value })
                        }
                        onBlur={() => commitUser(index, knownPeople)}
                        onKeyDown={(e) => handleKeyDown(e, index, "name")}
                      />
                    </Cell>
                  </td>
                  <td>
                    <Cell>
                      <input
                        ref={bindInput(`${user.id}:venmo`)}
                        placeholder="@venmo"
                        value={user.venmo || ""}
                        onChange={(e) =>
                          patchUser(index, {
                            venmo: e.target.value || undefined,
                          })
                        }
                        onBlur={() => commitUser(index, knownPeople)}
                        onKeyDown={(e) => handleKeyDown(e, index, "venmo")}
                      />
                    </Cell>
                  </td>
                  <td className="col-action">
                    <Cell>
                      {index !== 0 && (
                        <Trash2
                          className="mate-delete"
                          // keep the input's blur-commit from reindexing first
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => removeUser(index)}
                        />
                      )}
                    </Cell>
                  </td>
                </AnimatedRow>
              ))}
            </AnimatePresence>
          </tbody>
        </table>
      </div>

      <RecentMates suggestions={suggestions} onPick={handlePick} />
    </div>
  );
};

export default MatesTable;
